'use client'

import { SmartHeader } from '@/widgets/smart-header'
import { Footer } from '@/widgets/footer'
import { BuyForm } from '@/widgets/buy-form/ui/BuyForm'
import { CompaniesCatalog } from '@/widgets/companies-catalog'
import { motion } from 'framer-motion'
import { Calendar, TrendingUp, FileCheck, Shield, Zap, Award } from 'lucide-react'

// Metadata moved to layout or separate metadata file

export default function ReadyOOOPage() {
  return (
    <div className="relative flex min-h-screen flex-col">
      <SmartHeader />
      <main className="flex-1 pt-14">
        {/* Hero секция */}
        <section className="container pt-12 md:pt-20 pb-6 md:pb-8">
          <div className="mx-auto max-w-4xl text-center">
            <h1 className="mb-6 text-3xl md:text-5xl font-bold">Готовые ООО</h1>
            <p className="mb-8 text-xl text-muted-foreground">
              Каталог фирм без оборотов, с историей, лицензиями и банковскими счетами.
            </p>

            {/* Ключевые преимущества */}
            <div className="flex flex-wrap justify-center gap-4 md:gap-6 text-sm md:text-base text-muted-foreground">
              <div className="flex items-center gap-2">
                <span className="text-orange-500 text-lg">✓</span>
                <span>Готовые документы</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-orange-500 text-lg">✓</span>
                <span>Быстрый старт</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-orange-500 text-lg">✓</span>
                <span>Лицензии и СРО</span>
              </div>
            </div>
          </div>
        </section>

        {/* Форма заявки */}
        <section className="container py-3 bg-muted/20">
          <div className="mx-auto max-w-2xl">
            <BuyForm />
          </div>
        </section>

        {/* Виды готовых ООО */}
        <section className="container py-6 md:py-12 bg-muted/20">
          <div className="mx-auto max-w-6xl">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="text-2xl md:text-3xl font-bold text-center mb-4 md:mb-6"
            >
              Виды готовых ООО
            </motion.h2>

            <div className="grid md:grid-cols-3 gap-6 md:gap-8">
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                className="p-6 md:p-8 rounded-lg border hover:border-orange-500 transition-colors bg-background"
              >
                <div className="w-12 h-12 md:w-16 md:h-16 rounded-full bg-orange-100 dark:bg-orange-900/20 flex items-center justify-center mx-auto mb-4">
                  <Shield className="w-6 h-6 md:w-8 md:h-8 text-orange-500" />
                </div>
                <h3 className="text-lg md:text-xl font-semibold mb-4 text-center">
                  ООО без оборотов
                </h3>
                <p className="text-sm md:text-base text-muted-foreground mb-6 text-center">
                  Без оборотов и деятельности. Идеально для старта нового бизнеса
                </p>
                <ul className="text-sm space-y-2">
                  <li className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-orange-500" />
                    <span>Возраст от 1 года</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <FileCheck className="w-4 h-4 text-orange-500" />
                    <span>Нулевая отчетность</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Shield className="w-4 h-4 text-orange-500" />
                    <span>Готовые документы</span>
                  </li>
                  <li className="flex items-center gap-2 font-semibold text-orange-600">
                    <span className="text-orange-500">₽</span>
                    <span>От 150 000 ₽</span>
                  </li>
                </ul>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.1 }}
                className="p-6 md:p-8 rounded-lg border hover:border-orange-500 transition-colors bg-background relative"
              >
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-orange-500 text-white px-3 py-1 rounded-full text-xs font-semibold">
                  Популярно
                </div>
                <div className="w-12 h-12 md:w-16 md:h-16 rounded-full bg-orange-100 dark:bg-orange-900/20 flex items-center justify-center mx-auto mb-4">
                  <TrendingUp className="w-6 h-6 md:w-8 md:h-8 text-orange-500" />
                </div>
                <h3 className="text-lg md:text-xl font-semibold mb-4 text-center">
                  ООО с оборотом
                </h3>
                <p className="text-sm md:text-base text-muted-foreground mb-6 text-center">
                  С историей операций и оборотами. Подходят для участия в тендерах и получения
                  кредитов.
                </p>
                <ul className="text-sm space-y-2">
                  <li className="flex items-center gap-2">
                    <TrendingUp className="w-4 h-4 text-orange-500" />
                    <span>История операций</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <FileCheck className="w-4 h-4 text-orange-500" />
                    <span>Банковские счета</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Shield className="w-4 h-4 text-orange-500" />
                    <span>Кредитная история</span>
                  </li>
                  <li className="flex items-center gap-2 font-semibold text-orange-600">
                    <span className="text-orange-500">₽</span>
                    <span>От 250 000 ₽</span>
                  </li>
                </ul>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="p-6 md:p-8 rounded-lg border hover:border-orange-500 transition-colors bg-background"
              >
                <div className="w-12 h-12 md:w-16 md:h-16 rounded-full bg-orange-100 dark:bg-orange-900/20 flex items-center justify-center mx-auto mb-4">
                  <Award className="w-6 h-6 md:w-8 md:h-8 text-orange-500" />
                </div>
                <h3 className="text-lg md:text-xl font-semibold mb-4 text-center">
                  ООО с лицензиями
                </h3>
                <p className="text-sm md:text-base text-muted-foreground mb-6 text-center">
                  С готовыми лицензиями и СРО допусками. Сразу можете вести лицензируемую
                  деятельность.
                </p>
                <ul className="text-sm space-y-2">
                  <li className="flex items-center gap-2">
                    <Award className="w-4 h-4 text-orange-500" />
                    <span>Строительные СРО</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <FileCheck className="w-4 h-4 text-orange-500" />
                    <span>Медицинские лицензии</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Shield className="w-4 h-4 text-orange-500" />
                    <span>Образовательные</span>
                  </li>
                  <li className="flex items-center gap-2 font-semibold text-orange-600">
                    <span className="text-orange-500">₽</span>
                    <span>От 300 000 ₽</span>
                  </li>
                </ul>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Каталог компаний */}
        <section className="py-6 md:py-12">
          <div id="companies-catalog">
            <CompaniesCatalog />
          </div>
        </section>

        {/* Гарантии */}
        <section className="container py-6 md:py-12 bg-muted/20">
          <div className="mx-auto max-w-6xl">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="text-2xl md:text-3xl font-bold text-center mb-4 md:mb-6"
            >
              Что вы получаете
            </motion.h2>

            <div className="grid md:grid-cols-3 gap-6 md:gap-8">
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                className="flex items-start gap-3 md:gap-4 p-4 md:p-6 rounded-lg border hover:border-orange-500 transition-colors bg-background"
              >
                <div className="w-12 h-12 md:w-16 md:h-16 rounded-full bg-orange-100 dark:bg-orange-900/20 flex items-center justify-center flex-shrink-0">
                  <Shield className="w-6 h-6 md:w-8 md:h-8 text-orange-500" />
                </div>
                <div>
                  <h3 className="text-lg md:text-xl font-semibold mb-2">Готовые документы</h3>
                  <p className="text-sm md:text-base text-muted-foreground">
                    Передаем все необходимые документы, печати и банковские карточки
                  </p>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.1 }}
                className="flex items-start gap-3 md:gap-4 p-4 md:p-6 rounded-lg border hover:border-orange-500 transition-colors bg-background"
              >
                <div className="w-12 h-12 md:w-16 md:h-16 rounded-full bg-orange-100 dark:bg-orange-900/20 flex items-center justify-center flex-shrink-0">
                  <FileCheck className="w-6 h-6 md:w-8 md:h-8 text-orange-500" />
                </div>
                <div>
                  <h3 className="text-lg md:text-xl font-semibold mb-2">
                    Сопровождение после покупки
                  </h3>
                  <p className="text-sm md:text-base text-muted-foreground">
                    Помогаем с первыми шагами и консультируем в течение месяца бесплатно
                  </p>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="flex items-start gap-3 md:gap-4 p-4 md:p-6 rounded-lg border hover:border-orange-500 transition-colors bg-background"
              >
                <div className="w-12 h-12 md:w-16 md:h-16 rounded-full bg-orange-100 dark:bg-orange-900/20 flex items-center justify-center flex-shrink-0">
                  <Zap className="w-6 h-6 md:w-8 md:h-8 text-orange-500" />
                </div>
                <div>
                  <h3 className="text-lg md:text-xl font-semibold mb-2">Быстрое оформление</h3>
                  <p className="text-sm md:text-base text-muted-foreground">
                    Оформляем сделку в кратчайшие сроки с соблюдением всех формальностей
                  </p>
                </div>
              </motion.div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  )
}
